/**
 * LettersRegisterView (P272.3) -- the regulator-letters register.
 *
 * UX-2 J3-1b found there was no letters register: the "Letters" tab
 * opened the intake FORM, and the only way back to a received letter was
 * to remember which CAP it spawned. This is the list, newest-first, with
 * the closure rollup (how many CAPs respond + how many are approved) so a
 * user can see at a glance which letters are remediated.
 *
 * Reads GET /v1/framework/letters (open to all three positions). Rows link
 * to the letter detail; a prominent action intakes a new one.
 *
 * Mounted at /framework/letters in App.tsx.
 */
import { formatDate } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useLetters } from "framework/api/hooks";
import { MarsadIcon } from "shared/components/MarsadIcon";
import type { LetterSummary } from "shared/api/types";

function fmtDate(iso: string): string {
  try {
    return formatDate(iso, i18n.language);
  } catch {
    return iso;
  }
}

function ClosurePill({ letter }: { letter: LetterSummary }) {
  const { t } = useTranslation();
  const total = letter.cap_count ?? 0;
  const approved = letter.approved_cap_count ?? 0;
  if (total === 0) {
    return (
      <span
        data-testid="letters-closure"
        data-state="none"
        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500"
      >
        {t("letters.register.noCaps")}
      </span>
    );
  }
  const done = approved === total;
  return (
    <span
      data-testid="letters-closure"
      data-state={done ? "remediated" : "in-flight"}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        done ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"
      }`}
    >
      {t("letters.register.capRollup", { approved, total })}
    </span>
  );
}

export function LettersRegisterView() {
  const { t } = useTranslation();
  const letters = useLetters();

  return (
    <section
      data-testid="letters-register"
      className="mx-auto max-w-4xl space-y-5 px-4 py-6"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500">
            {t("letters.register.kicker")}
          </p>
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="obligation"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("letters.register.heading")}
          </h1>
        </div>
        <Link
          to="/framework/letters/new"
          data-testid="letters-intake-link"
          className="whitespace-nowrap rounded border border-brand-brassDark bg-brand-brass px-3 py-1.5 text-sm font-semibold text-brand-navy"
        >
          {t("letters.register.intake")}
        </Link>
      </header>

      {letters.isLoading && (
        <p className="text-sm text-slate-400">{t("letters.register.loading")}</p>
      )}
      {letters.isError && (
        <p className="text-sm text-rag-red">{t("letters.register.error")}</p>
      )}
      {letters.data && letters.data.length === 0 && (
        <p data-testid="letters-empty" className="text-sm text-slate-500">
          {t("letters.register.empty")}
        </p>
      )}

      {letters.data && letters.data.length > 0 && (
        <table className="w-full text-start text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="py-1">{t("letters.register.colLetter")}</th>
              <th className="py-1">{t("letters.register.colReceived")}</th>
              <th className="py-1 text-end">{t("letters.register.colObservations")}</th>
              <th className="py-1 text-end">{t("letters.register.colClosure")}</th>
            </tr>
          </thead>
          <tbody>
            {letters.data.map((l) => (
              <tr
                key={l.id}
                data-testid="letters-register-row"
                className="border-t border-slate-100"
              >
                <td className="py-2">
                  <Link
                    to={`/framework/letters/${l.id}`}
                    data-testid={`letters-row-link-${l.id}`}
                    className="font-medium text-brand-navy hover:text-brand-brass"
                  >
                    {l.title}
                  </Link>
                  {" · "}
                  <span className="ms-2 text-xs text-slate-400">
                    {l.regulator_code}
                  </span>
                </td>
                <td className="py-2 text-slate-600 tabular-nums">
                  {fmtDate(l.received_at)}
                </td>
                <td className="py-2 text-end tabular-nums text-slate-600">
                  {l.observation_count}
                </td>
                <td className="py-2 text-end">
                  <ClosurePill letter={l} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
